import {useQuery} from "@tanstack/react-query";
import {getCarouselsGamesService} from "../../services/games/carousels/getCarouselsGames.service";
import {parseCarouselsFromResponse} from "../../util/carousels/parseCarouselsResponse";
import {isVisibleGame} from "../../util/hiddenGames";

const DEFAULT_PARAMS = {
  page: 1,
  page_size: 50,
  with_games: "true",
  sort: "desc",
};

const sortByDisplayOrder = (a, b) => (a.display_order ?? 0) - (b.display_order ?? 0);

const normalizeCarouselGames = carousel => {
  const rawGames = carousel.games ?? carousel.carousel_games ?? [];
  if (!Array.isArray(rawGames)) return [];
  return [...rawGames].filter(isVisibleGame).sort(sortByDisplayOrder);
};

const normalizeCarousels = responseData => {
  const carousels = parseCarouselsFromResponse(responseData);

  return carousels
    .filter(carousel => carousel.is_active !== false)
    .map(carousel => ({
      ...carousel,
      games: normalizeCarouselGames(carousel),
    }))
    .sort(sortByDisplayOrder);
};

export const useGetCarouselsGames = (params = {}) => {
  const queryParams = {...DEFAULT_PARAMS, ...params};

  const {data, isPending, isError, error, refetch, isFetching} = useQuery({
    queryKey: ["carouselsGames", queryParams],
    queryFn: async () => {
      const response = await getCarouselsGamesService(queryParams);
      return normalizeCarousels(response);
    },
    staleTime: 5 * 60 * 1000,
  });

  const carouselsWithGames = (data ?? []).filter(carousel => carousel.games.length > 0);

  if (import.meta.env.DEV && (data?.length ?? 0) > 0 && carouselsWithGames.length === 0) {
    console.warn(
      "[CustomCarousels] Carousels loaded but none have games. " + "Associate games in BO and use ?with_games=true."
    );
  }

  return {
    carousels: carouselsWithGames,
    allCarousels: data ?? [],
    isPending: isPending || isFetching,
    isError,
    error,
    refetch,
  };
};
